import { Document, Font, Image, PDFDownloadLink, PDFViewer, Page, Text, View } from "@react-pdf/renderer";

import { Modal } from "antd";
import React from "react";
import { createInvoiceStyles } from "../../../styles/pdf/invoice";
import fontChinese from "../../../assets/ZhuoJianGanLanJianTiRegular.ttf";
import fontThai from "../../../assets/THSarabunNewRegular.ttf";
// import fontChinese from "../../../assets/NotoSerifCJKsc-Regular.otf";
import { renderMultilingualText } from "../../../utils/renderMultilingualText";
import { formatNumber } from "../../../utils/number";
import { numberToThaiCurrency } from "../../../utils/utils";
import { resolveImageUrl } from "../../../utils/imageUrl";

Font.register({
  family: "Noto Sans Thai",
  src: fontThai,
});

Font.register({
  family: "ZhuoJianGanLanJianTi Regular",
  src: fontChinese,
});

function billLineTotalWithOt(list) {
  const ship = Number(list?.bill_amount || 0);
  return ship;
}

/**
 * Pagination strategy (single, consistent mechanism):
 * - <View fixed> renders the same header on EVERY page (page 1 → last).
 * - Page paddingTop = HEADER_RESERVE_PT reserves space so body never overlaps header.
 * - Each table row is wrap={false} → rows never split across pages.
 * - TOTAL + payment + terms are wrapped in ONE wrap={false} block → they always
 *   stay together. If there's no room on the last data page, the whole TOTAL
 *   block moves to a fresh page (still with the fixed header on top).
 */
const HEADER_TOP_PT = 10;
const HEADER_HEIGHT_PT = 250;
const HEADER_RESERVE_PT = HEADER_TOP_PT + HEADER_HEIGHT_PT;
const FOOTER_BOTTOM_PT = 30;
const TABLE_ROW_MIN_HEIGHT = 20;

function BillTableHeader({ invoiceStyles }) {
  return (
    <View
      style={[
        invoiceStyles.tableRow,
        invoiceStyles.textBold,
        invoiceStyles.textMs,
        invoiceStyles.tableBorder,
        invoiceStyles.table,
      ]}
    >
      <View style={invoiceStyles.tableColNO}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellCenter]}>NO.</Text>
      </View>
      <View style={invoiceStyles.tableColDESC}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellCenter]}>DESCRIPTION</Text>
      </View>
      <View style={invoiceStyles.tableColREF}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellCenter]}>REFERENCE</Text>
      </View>
      <View style={invoiceStyles.tableColTYPE}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellCenter]}>TYPE</Text>
      </View>
      <View style={invoiceStyles.tableColCN}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>C/N</Text>
      </View>
      <View style={invoiceStyles.tableColKG}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>KG</Text>
      </View>
      <View style={invoiceStyles.tableColL}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>L</Text>
      </View>
      <View style={invoiceStyles.tableColW}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>W</Text>
      </View>
      <View style={invoiceStyles.tableColH}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>H</Text>
      </View>
      <View style={invoiceStyles.tableColCBM}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>CBM</Text>
      </View>
      <View style={invoiceStyles.tableColRATE}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>RATE</Text>
      </View>
      <View style={invoiceStyles.tableColPRICE}>
        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>PRICE(฿)</Text>
      </View>
    </View>
  );
}

function BillPageHeaderInfo({ row, invoiceStyles }) {
  const stImage =
    row.app_company?.company_bill_type === "ST"
      ? row.app_company.app_companyimage?.find((img) => img.image?.includes("ST_CompanyBill"))
      : null;

  return (
    <View>
      <View
        style={[
          invoiceStyles.container,
          {
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginTop: 8,
          },
        ]}
      >
        <View style={[invoiceStyles.section, { width: "75%" }]}>
          <Text style={invoiceStyles.text}>
            {row.app_company_type_st !== null ? row.app_company_type_st?.company_name : row.app_company?.company_name}
          </Text>
          {/* <Text style={invoiceStyles.text}>
            {row.app_company_type_st !== null
              ? row.app_company_type_st?.company_address
              : row.app_company?.company_address}
          </Text> */}
          {row.app_company_type_st !== null
            ? renderMultilingualText(row.app_company_type_st?.company_address, invoiceStyles)
            : renderMultilingualText(row.app_company?.company_address, invoiceStyles)}
          <Text style={invoiceStyles.text}>
            {row.app_company_type_st !== null ? row.app_company_type_st?.telephone : row.app_company?.telephone}
          </Text>
        </View>
        <View
          style={[
            invoiceStyles.section,
            {
              width: "25%",
              alignItems: "center",
              justifyContent: "center",
            },
          ]}
        >
          {row.app_company?.company_bill_type === "ST" ? (
            stImage ? (
              <Image
                style={[
                  invoiceStyles.image,
                  {
                    width: 100,
                    height: 60,
                    objectFit: "contain",
                  },
                ]}
                src={resolveImageUrl(stImage.image)}
              />
            ) : (
              <Image
                style={[
                  invoiceStyles.image,
                  {
                    width: 100,
                    height: 60,
                    objectFit: "contain",
                  },
                ]}
                src="default_image_url"
              />
            )
          ) : (
            row.app_company?.app_companyimage?.[0] && (
              <Image
                style={[
                  invoiceStyles.image,
                  {
                    width: 100,
                    height: 60,
                    objectFit: "contain",
                  },
                ]}
                src={resolveImageUrl(row.app_company.app_companyimage[0].image)}
              />
            )
          )}
        </View>
      </View>

      <View style={[invoiceStyles.infoBoxWhite, invoiceStyles.table, { alignItems: "center", marginVertical: 6 }]}>
        <View style={invoiceStyles.infoBoxHeader}>
          <Text style={invoiceStyles.infoBoxTitle}>ข้อมูลการใช้บริการ</Text>
        </View>
        <View style={invoiceStyles.padding}>
          <View style={invoiceStyles.container}>
            <View style={invoiceStyles.column}>
              <Text style={invoiceStyles.text}>Company Id: {row.app_company?.company_code}</Text>
              <Text style={invoiceStyles.textMs}>{row.app_company?.company_name}</Text>
              {/* <Text style={[invoiceStyles.textMs]}>{row.app_company?.company_address}</Text> */}
              {renderMultilingualText(row.app_company?.company_address, invoiceStyles)}
              <Text style={invoiceStyles.textMs}>{row.app_company?.telephone}</Text>
            </View>
            <View style={invoiceStyles.column}>
              <Text style={invoiceStyles.text}>Shipping Detail:</Text>
              <Text style={invoiceStyles.textMs}>
                Origin:{" "}
                {
                  row.app_origintransportation?.app_warehouse_app_origintransportation_origin_idToapp_warehouse
                    ?.warehouse_code
                }
              </Text>
              <Text style={invoiceStyles.textMs}>Container no: {row.app_origintransportation?.container}</Text>
              <Text style={invoiceStyles.textMs}>
                Shipping type: {row.app_origintransportation?.route?.toUpperCase()}
              </Text>
              <Text style={invoiceStyles.textMs}>Sale Person: -</Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

function BillPageHeader({ row, invoiceStyles }) {
  return (
    <View>
      <BillPageHeaderInfo row={row} invoiceStyles={invoiceStyles} />
    </View>
  );
}

export const CompanyBiller = ({ setIsOpenModal, isOpenModal, data, version }) => {
  const handleCancel = () => {
    setIsOpenModal(false);
  };
  return (
    <>
      <Modal open={isOpenModal} width={1024} onCancel={handleCancel} onOk={() => setIsOpenModal(false)}>
        <PDFViewer width="100%" height="600" key={`viewer-${version}`}>
          <MyDocument data={data} key={`doc-${version}`} />
        </PDFViewer>
      </Modal>
      <PDFDownloadLink
        document={<MyDocument data={data} key={`dl-doc-${version}`} />}
        fileName={`example-${version}.pdf`}
      />
    </>
  );
};

const MyDocument = ({ data }) => {
  const columnConfig = {
    NO: 5,
    DESC: 22,
    REF: 12,
    TYPE: 6,
    CN: 6,
    KG: 7,
    L: 6,
    W: 6,
    H: 6,
    CBM: 8,
    RATE: 8,
    PRICE: 8,
    TotalLabel: 27,
  };

  const invoiceStyles = createInvoiceStyles(columnConfig);

  return (
    <>
      {data.map((row) => {
        const items = (row?.app_companybillitem ?? []).filter((list) => {
          const billAmount = Number(list?.bill_amount ?? 0);
          return !((list?.description === "origin_package_service" && billAmount === 0) || list?.description === null);
        });

        return (
          <Document key={row.id}>
            <Page
              size="A4"
              wrap
              style={[
                invoiceStyles.page,
                {
                  backgroundColor: "#FFFFFF",
                  paddingTop: HEADER_RESERVE_PT,
                  paddingBottom: FOOTER_BOTTOM_PT,
                  paddingHorizontal: 20,
                },
              ]}
            >
              {/* FIXED HEADER — renders on every page (page 1 → last) */}
              <View
                fixed
                style={{
                  position: "absolute",
                  top: HEADER_TOP_PT,
                  left: 20,
                  right: 20,
                  backgroundColor: "#FFFFFF",
                }}
              >
                <BillPageHeader row={row} invoiceStyles={invoiceStyles} />
              </View>

              {/* TABLE BODY — rows wrap naturally; each row stays intact (wrap={false}) */}
              <View>
                <BillTableHeader invoiceStyles={invoiceStyles} />
              </View>
              <View style={[invoiceStyles.table, { padding: 0 }]}>
                {items.map((list, index) => {
                  const pkg = list?.app_originpackagetransportation?.app_originpackage;
                  const bodyCellPad = { paddingVertical: 3 };

                  return (
                    <View
                      key={list.id}
                      wrap={false}
                      style={[
                        invoiceStyles.tableRow,
                        invoiceStyles.textMs,
                        invoiceStyles.tableRowItem,
                        { minHeight: TABLE_ROW_MIN_HEIGHT },
                      ]}
                    >
                      <View style={invoiceStyles.tableColNO}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight, bodyCellPad]}>
                          {index + 1}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColDESC}>
                        <Text
                          style={[
                            invoiceStyles.tableCell,
                            invoiceStyles.fontChinese,
                            invoiceStyles.textXs,
                            invoiceStyles.tableCellCenter,
                            bodyCellPad,
                          ]}
                        >
                          {pkg?.package_description}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColREF}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellCenter, bodyCellPad]}>
                          {pkg?.reference}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColTYPE}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellCenter, bodyCellPad]}>
                          {pkg?.package_type}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColCN}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight, bodyCellPad]}>
                          {pkg?.total_count || 0}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColKG}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight, bodyCellPad]}>
                          {/* {(Math.ceil(Number(pkg?.total_weight || 0) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((pkg?.total_weight || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColL}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight, bodyCellPad]}>
                          {/* {pkg?.length || 0} */}
                          {formatNumber(Math.round((pkg?.length || 0) * 100) / 100, 0)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColW}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight, bodyCellPad]}>
                          {/* {pkg?.width || 0} */}
                          {formatNumber(Math.round((pkg?.width || 0) * 100) / 100, 0)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColH}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight, bodyCellPad]}>
                          {/* {pkg?.height || 0} */}
                          {formatNumber(Math.round((pkg?.height || 0) * 100) / 100, 0)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColCBM}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight, bodyCellPad]}>
                          {/* {(Math.ceil(Number(pkg?.total_volume || 0) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((pkg?.total_volume || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColRATE}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight, bodyCellPad]}>
                          {/* {(Math.ceil(Number(list?.rate || 0) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((list?.rate || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.tableColPRICE}>
                        <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight, bodyCellPad]}>
                          {/* {(Math.ceil(billLineTotalWithOt(list) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((billLineTotalWithOt(list) || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                    </View>
                  );
                })}
              </View>

              {/* TOTAL + PAYMENT + TERMS — single wrap={false} block: always stays together.
                  If no room on last data page, the whole block moves to a fresh page
                  (fixed header still renders on top). */}
              <View wrap={false}>
                {/* TOTAL row */}
                <View
                  style={[invoiceStyles.tableRow, invoiceStyles.textMs, invoiceStyles.tableRowItem, { padding: 0 }]}
                >
                  <View style={invoiceStyles.tableColTotalLabel}>
                    <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellCenter]}>TOTAL</Text>
                  </View>
                  <View style={invoiceStyles.tableColREF}>
                    <Text style={invoiceStyles.tableCell}></Text>
                  </View>
                  <View style={invoiceStyles.tableColTYPE}>
                    <Text style={invoiceStyles.tableCell}></Text>
                  </View>
                  <View style={invoiceStyles.tableColCN}>
                    <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>
                      {/* {(Math.ceil(Number(row?.total_count || 0) * 100) / 100).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} */}
                      {formatNumber(Math.round((row?.total_count || 0) * 100) / 100, 0)}
                    </Text>
                  </View>
                  <View style={invoiceStyles.tableColKG}>
                    <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>
                      {/* {(Math.ceil(Number(row?.total_weight || 0) * 100) / 100).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} */}
                      {formatNumber(Math.round((row?.total_weight || 0) * 100) / 100, 2)}
                    </Text>
                  </View>
                  <View style={invoiceStyles.tableColL}>
                    <Text style={invoiceStyles.tableCell}></Text>
                  </View>
                  <View style={invoiceStyles.tableColW}>
                    <Text style={invoiceStyles.tableCell}></Text>
                  </View>
                  <View style={invoiceStyles.tableColH}>
                    <Text style={invoiceStyles.tableCell}></Text>
                  </View>
                  <View style={invoiceStyles.tableColCBM}>
                    <Text style={[invoiceStyles.tableCell, invoiceStyles.tableCellRight]}>
                      {/* {(Math.ceil(Number(row?.package_total_volume || 0) * 100) / 100).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} */}
                      {formatNumber(Math.round((row?.package_total_volume || 0) * 100) / 100, 2)}
                    </Text>
                  </View>
                  <View style={invoiceStyles.tableColRATE}>
                    <Text style={invoiceStyles.tableCell}></Text>
                  </View>
                  <View style={invoiceStyles.tableColPRICE}>
                    <Text style={[invoiceStyles.tableCell, invoiceStyles.textRight]}>
                      {/* {(Math.ceil(Number(row?.sum_total_bill || 0) * 100) / 100).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} */}
                      {formatNumber(Math.round((row?.sum_total_bill || 0) * 100) / 100, 2)}
                    </Text>
                  </View>
                </View>

                {/* Payment block */}
                <View style={[invoiceStyles.padding]}>
                  <View style={invoiceStyles.container}>
                    <View style={invoiceStyles.column}>
                      <Text style={invoiceStyles.textMd}>{"PAYMENT METHOD"}:</Text>
                      <Text style={invoiceStyles.textMd}>{"BY BANK TRANSFER"}</Text>
                      <Text style={invoiceStyles.textMd}>{row.app_company?.bank_account_company_bill}</Text>
                    </View>

                    <View style={invoiceStyles.column}>
                      <View style={invoiceStyles.costRow}>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costLabelIndent]}>SHIPPING COST:</Text>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costValue]}>
                          {/* {(Math.ceil(Number(row?.package_total_bill_amount || 0) * 100) / 100).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )} */}
                          {formatNumber(Math.round((row?.package_total_bill_amount || 0) * 100) / 100, 2)}
                        </Text>
                      </View>

                      <View style={invoiceStyles.costRow}>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costLabelIndent]}>Wooden Create:</Text>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costValue]}>
                          {/* {(Math.ceil(Number(row?.total_wc || 0) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((row?.total_wc || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.costRow}>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costLabelIndent]}>China Delivery:</Text>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costValue]}>
                          {/* {(Math.ceil(Number(row?.total_od || 0) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((row?.total_od || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.costRow}>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costLabelIndent]}>Thai Delivery:</Text>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costValue]}>
                          {/* {(Math.ceil(Number(row?.total_dd || 0) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((row?.total_dd || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.costRow}>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costLabelIndent]}>Insurance:</Text>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costValue]}>
                          {/* {(Math.ceil(Number(row?.total_in || 0) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((row?.total_in || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                      <View style={invoiceStyles.costRow}>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costLabelIndent]}>Other fee:</Text>
                        <Text style={[invoiceStyles.textMd, invoiceStyles.costValue]}>
                          {/* {(Math.ceil(Number(row?.total_ot || 0) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((row?.total_ot || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                    </View>
                  </View>

                  <View style={[invoiceStyles.paddingMs]}>
                    <View style={invoiceStyles.container}>
                      <View style={invoiceStyles.column}>
                        <Text style={[invoiceStyles.text, invoiceStyles.textCenter]}>
                          {numberToThaiCurrency(parseFloat(row?.sum_total_bill || 0))}
                        </Text>
                      </View>
                      <View style={invoiceStyles.column}>
                        <Text style={[invoiceStyles.text, invoiceStyles.textCenter]}></Text>
                      </View>
                      <View style={invoiceStyles.column}>
                        <Text style={[invoiceStyles.text, invoiceStyles.textCenter]}></Text>
                      </View>
                      <View style={invoiceStyles.column}>
                        <Text style={[invoiceStyles.text, invoiceStyles.textCenter]}></Text>
                      </View>
                      <View style={invoiceStyles.column}>
                        <Text style={[invoiceStyles.text, invoiceStyles.textCenter]}>Total</Text>
                      </View>
                      <View style={invoiceStyles.column}>
                        <Text style={[invoiceStyles.text, invoiceStyles.textRight]}>
                          {/* {(Math.ceil(Number(row?.sum_total_bill || 0) * 100) / 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} */}
                          {formatNumber(Math.round((row?.sum_total_bill || 0) * 100) / 100, 2)}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>

                {/* Terms */}
                <View
                  style={{
                    marginTop: 10,
                    left: 5,
                    right: 5,
                  }}
                >
                  <Text style={[invoiceStyles.textMd, invoiceStyles.textBold]}>Term & condition</Text>
                  <Text style={invoiceStyles.textMd}>
                    กรณีสินค้ามีปัญหาหลังจากได้รับสินค้าแล้ว กรุณาแจ้งทางบริษัทฯ ภายใน 3 วัน
                  </Text>
                  <Text style={invoiceStyles.textMd}>
                    มิฉะนั้น ทางบริษัทฯ ขอปฏิเสธความรับผิดชอบ ไม่ว่าจะกรณีใดๆ ทั้งสิ้น
                  </Text>
                </View>
              </View>
            </Page>
          </Document>
        );
      })}
    </>
  );
};
